import { APP_STEP, step } from '../actions/app'
import { sendIceCandidate, descriptionsInSync, sendOffer } from '../actions/signaling'
import {
  resetVideoStream,
  resetAudioStream,
  iceConnectionState,
  accessPending,
  accessGranted,
  connectionUnstable,
} from '../actions/webrtcConnection'
import { AGENT_TYPE } from './../actions/agent'
import { audioChatStatsResponse } from '../actions/media'

import { CommandsMessageParser } from './parse/CommandsMessageParser'
import { MouseMessageParser } from './parse/MouseMessageParser'
import { raiseError, setDisconnectMessage, gracefulExit } from '../actions/disconnect'
import { logger } from './utils/logger'
import { parse, write } from 'sdp-transform'
import { safariWithVP8Codec } from '../lib/utils/browser'
import { Permissions } from './Permissions'

const turnUserName = 'MCbPpqq9wyewCv8RZz7xtqbIAjOvq8XH'
const turnPassword = 'jSpZL2k04J2ACD2fNfN5dNcTJolfH6os'

const STABLE_TIMEOUT = 1
const UNSTABLE_TIMEOUT = 1
export const CLOSE_TIMEOUT = 20

export class WebRTC {
  static create (store, RTCPeerConnectionType = RTCPeerConnection, CommandsMessageParserType = CommandsMessageParser,
    MouseMessageParserType = MouseMessageParser) {
    const commandsMessageParser = new CommandsMessageParserType()
    const mouseMessageParser = new MouseMessageParserType()
    const instance = new WebRTC(store, RTCPeerConnectionType, commandsMessageParser, mouseMessageParser)
    return instance
  }

  constructor (store, RTCPeerConnectionType, commandsMessageParser, mouseMessageParser) {
    this.RTCPeerConnectionType = RTCPeerConnectionType
    this.dispatch = store.dispatch
    this.getState = store.getState
    this.commandsMessageParser = commandsMessageParser
    this.mouseMessageParser = mouseMessageParser

    this.commandChannel = null
    this.keyboardChannel = null
    this.mouseChannel = null
    this.webRTCConnection = null
    this.earlyIceCandidates = []
    this.connectionUnstable = null
    this.audioTransceiver = null
    this._audioChatTrack = null
    this._willClose = false
  }

  willClose () {
    this._willClose = true
  }

  raiseError ({ message, error }) {
    const suffix = (Permissions.agentType === AGENT_TYPE.INSTALLED_AGENT) ? '-unattended' : ''
    this.dispatch(raiseError({ message: `${message}${suffix}`, error }))
  }

  // ..........................
  // ... public

  onClosePeerConnection = () => {
    this._resetUnstableTimers()

    if (this.webRTCConnection) {
      try {
        this.keyboardChannel.close()
        this.keyboardChannel = null

        this.mouseChannel.close()
        this.mouseChannel = null

        this.commandChannel.close()
        this.commandChannel = null

        this.webRTCConnection.close()
      } catch (error) {
        //
      }
    }
  }

  synchronizeDescriptions = (payload) => {
    const remoteDescription = payload.agentDescription
    const localDescription = this.getState().signaling.clientDescription
    logger('====================== AGENT SDP =============================')
    // logger(remoteDescription.sdp)
    // logger('==============================================================')

    this.webRTCConnection.setLocalDescription(localDescription)
      .catch((/* errorEvent */) => {
        this.raiseError({ message: 'error-rtc-connection', error: 'WebRTC setLocalDescription failed' })
      })

    this.webRTCConnection.setRemoteDescription(remoteDescription)
      .then(() => {
        this.earlyIceCandidates.forEach((candidate) => {
          this.webRTCConnection.addIceCandidate(candidate)
        })
        this.earlyIceCandidates = []

        this.dispatch(descriptionsInSync())
      })
      .catch((/* errorEvent */) => {
        this.raiseError({ message: 'error-rtc-connection', error: 'WebRTC setRemoteDescription failed' })
      })
  }

  /**
   *
   * @param {* signaling.ICECandidates} payload
   */
  addIceCandidate = (payload) => {
    if (payload) {
      const rtcIceCandidateInit = payload
      if (this.webRTCConnection.remoteDescription) {
        setTimeout(() => {
          this.webRTCConnection.addIceCandidate(rtcIceCandidateInit)
            .catch((/* errorEvent */) => {
              this.raiseError({ message: 'error-rtc-connection', error: 'WebRTC addIceCandidate failed' })
            })
        }, 0)
      } else {
        this.earlyIceCandidates.push(rtcIceCandidateInit)
      }
    }
  }

  createOffer = async ({ agentDetails } = {}) => {
    this._willClose = false
    const configuration = WebRTC._createRTCConfiguration(this.getState())
    this.webRTCConnection = new this.RTCPeerConnectionType(configuration)
    this.webRTCConnection.onicecandidate = this._webRTCConnectionOnIceCandidate
    // this.webRTCConnection.addEventListener('icecandidateerror', (event) => {
    //   logger('icecandidateerror', event)
    // })
    this.webRTCConnection.ontrack = this._webRTCConnectionOnTrack
    this.webRTCConnection.oniceconnectionstatechange = this._webRTCConnectionOnIceConnectionStateChange
    this.webRTCConnection.onnegotiationneeded = () => logger('==== onnegotiationneeded  ===')

    this.commandChannel = this.webRTCConnection.createDataChannel('command')
    this.commandChannel.onmessage = this._commandChannelOnMessage
    this.keyboardChannel = this.webRTCConnection.createDataChannel('keyboard', { ordered: true })
    this.mouseChannel = this.webRTCConnection.createDataChannel('mouse')
    this.mouseChannel.onmessage = this._mouseChannelOnMessage

    const { features } = this.getState()
    try {
      this.webRTCConnection.addTransceiver('video')

      if (features.audioChat) {
        // add audio track (silence) and get it's audio transceiver
        const { audio: { silenceAudio: { stream, track } } } = this.getState()
        try {
          this.webRTCConnection
            .addTrack(track, stream)
          this.audioTransceiver = this.webRTCConnection
            .getTransceivers()
            .find((t) => (t.sender.track && t.sender.track.kind === 'audio'))
        } catch (error) {
          logger(error)
        }
      }
      const offer = await this.webRTCConnection.createOffer()
      try {
        offer.sdp = WebRTC._processSdp(offer.sdp, { agentDetails })
      } catch (e) {
        logger(e)
      }
      // logger('======= client sdp offer =====\n', offer.sdp)
      logger('======= client sdp offer =====\n')
      this.dispatch(sendOffer(offer))
    } catch (errorEvent) {
      this.raiseError({ message: 'error-rtc-connection', error: 'WebRTC createOffer failed' })
    }
  }

  async setAudioChatTrack (track) {
    // logger('==== webrtc replace track', track)
    if (this.audioTransceiver && this._audioChatTrack !== track) {
      try {
        await this.audioTransceiver.sender.replaceTrack(track)
        this._audioChatTrack = track
      } catch (error) {
        logger('error replacing track', error)
      }
    }
  }

  sendKeyboardEvent = (payload) => {
    if (!this._willClose && this.keyboardChannel && this.keyboardChannel.readyState === 'open') {
      this.keyboardChannel.send(payload)
    }
  }

  sendMouseEvent = (payload) => {
    if (!this._willClose && this.mouseChannel && this.mouseChannel.readyState === 'open') {
      try {
        this.mouseChannel.send(payload)
      } catch (error) {
        logger(' ! error sending mouse', error)
      }
    }
  }

  sendCommand = (protobufMessage) => {
    if (!this._willClose && this.commandChannel && this.commandChannel.readyState === 'open') {
      try {
        this.commandChannel.send(protobufMessage.buffer)
      } catch (error) {
        logger(' ! error sending command', error)
      }
    }
  }

  audioChatStatsRequest = () => {
    if (this.audioTransceiver) {
      const pOutput = this.audioTransceiver.sender.getStats()
      const pInput = this.audioTransceiver.receiver.getStats()
      Promise.all([pOutput, pInput])
        .then(([outputStats, inputStats]) => this.dispatch(audioChatStatsResponse({
          outputStats,
          inputStats,
        })))
    }
  }

  // ..........................
  // ... private

  static _createRTCConfiguration (state) {
    let stunList = []
    state.signaling.stunList
      .filter((item) => item)
      .forEach((item) => {
        stunList.push({
          urls: item
        })
      })

    const { features: { FEATURE_DYNAMIC_TURN_AUTH } } = state
    let turnList = []
    if (FEATURE_DYNAMIC_TURN_AUTH) {
      turnList = state.signaling.turnServersList.map(({
        urlsList,
        username,
        credential,
      }) => ({
        urls: urlsList,
        username,
        credential,
      }))
    } else {
      state.signaling.turnList.forEach((item) => {
        turnList.push({
          urls: item,
          username: turnUserName,
          credential: turnPassword
        })
      })
    }

    const configuration = {
      // An array of RTCIceServer objects
      iceServers: [
        ...turnList,
        ...stunList,
      ],
      // iceTransportPolicy: 'relay'
    }
    return configuration
  }

  static _convert (data) {
    return new Promise((resolve/* , reject*/) => {
      if (data instanceof Blob) {
        let fileReader = new FileReader()
        fileReader.onloadend = () => {
          resolve(fileReader.result)
        }
        fileReader.readAsArrayBuffer(data)
      } else {
        resolve(data)
      }
    })
  }

  static _processSdp (sdpString, { agentDetails = {} } = {}) {
    const sdp = parse(sdpString)

    const mediaAudio = sdp.media.find((m) => (m.type === 'audio'))
    const opusRtp = mediaAudio ? mediaAudio.rtp.find((rtp) => (rtp.codec === 'opus')) : null
    const opusPayload = opusRtp ? opusRtp.payload : null

    if (opusPayload) {
      // keep only opus audio codec, good audio quality params
      const bytes = 64 * 1024 // opus max 510000
      const stereo = 0 // 0, 1
      const sampling = 48000
      mediaAudio.payloads = `${opusPayload}`
      mediaAudio.fmtp = [
        {
          payload: opusPayload,
          config: [
            'useinbandfec=1',
            `maxaveragebitrate=${bytes}`,
            `sprop-maxcapturerate=${sampling}`,
            `maxplaybackrate=${sampling}`,
            `stereo=${stereo}`,
            `sprop-stereo=${stereo}`,
            'cbr=0',
          ].join(';'),
        },
      ]
      // rtcp feedback attr may not be present
      if (mediaAudio.rtcpFb) {
        mediaAudio.rtcpFb = mediaAudio.rtcpFb.filter((rtcpFb) => (rtcpFb.payload === opusPayload))
      }
      mediaAudio.rtp = [opusRtp]
      mediaAudio.bandwidth = [{ type: 'AS', limit: (bytes * (stereo + 1)) }]
    }

    // if safari >= 12 and ios keep only VP8 video codec
    logger('SDP process - keep only vp8', safariWithVP8Codec, agentDetails.platformType)
    if (safariWithVP8Codec && (agentDetails.platformType === 'ios')) {
      const mediaVideo = sdp.media.find((m) => (m.type === 'video'))
      const vp8Rtp = mediaVideo ? mediaVideo.rtp.find((rtp) => (rtp.codec === 'VP8')) : null
      const vp8Payload = vp8Rtp ? vp8Rtp.payload : null
      if (vp8Payload) {
        mediaVideo.payloads = `${vp8Payload}`
        mediaVideo.rtp = [vp8Rtp]
        if (mediaVideo.rtcpFb) {
          mediaVideo.rtcpFb = mediaVideo.rtcpFb.filter((rtcpFb) => (rtcpFb.payload === vp8Payload))
        }
        if (mediaVideo.fmtp) {
          mediaVideo.fmtp = mediaVideo.fmtp.filter((fmtp) => (fmtp.payload === vp8Payload))
        }
      }
    }

    return write(sdp)
  }

  _commandChannelOnMessage = async (messageEvent) => {
    const result = await WebRTC._convert(messageEvent.data)
    const action = this.commandsMessageParser.parse(result)
    logger('command action', action)
    this.dispatch(action)
  }

  _mouseChannelOnMessage = async (messageEvent) => {
    const result = await WebRTC._convert(messageEvent.data)
    const action = this.mouseMessageParser.parse(result)

    this.dispatch(action)
  }

  _webRTCConnectionOnTrack = (event) => {
    const { features } = this.getState()

    if (event.track.kind === 'video') {
      logger('rec video track')
      this.dispatch(resetVideoStream(event.streams[0]))
    }

    if (features.audioChat) {
      if (event.track.kind === 'audio') {
        logger('rec audio track')
        this.dispatch(resetAudioStream(event.streams[0]))
      }
    }
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnectionIceEvent
  _webRTCConnectionOnIceCandidate = (rtcPeerConnectionIceEvent) => {
    const rtcIceCandidate = rtcPeerConnectionIceEvent.candidate
    if (rtcIceCandidate) {
      // on the other side RTCIceCandidate.toJSON() can be passed into RTCPeerConnection.addIceCandidate(),
      // so this will be the payload of the ICECandidates message
      this.dispatch(sendIceCandidate(rtcIceCandidate))
    } else {
      // @TODO: Calculate connection timeout from here - if no addTrack / addDataChannel arrives in N seconds, raise error.
      // @TODO: Create a reset connection message so that if handshake fails, it can be restarted.
    }
  }

  _resetUnstableTimers () {
    if (this.connectionUnstable) {
      if (this.connectionUnstable.unstableTimer) {
        clearTimeout(this.connectionUnstable.unstableTimer)
      }
      if (this.connectionUnstable.closeTimer) {
        clearTimeout(this.connectionUnstable.closeTimer)
      }
    }
  }

  _resetConnectionUnstable () {
    this._resetUnstableTimers()
    this.connectionUnstable = {
      value: false,
      unstableTimer: null,
      closeTimer: null,
    }
  }

  _computeConnectionUnstable (connected) {

    // reset unstable data if not present
    if (!this.connectionUnstable) {
      this._resetConnectionUnstable()
    }
    // delete the unstableTimer
    if (this.connectionUnstable.unstableTimer) {
      clearTimeout(this.connectionUnstable.unstableTimer)
      this.connectionUnstable.unstableTimer = null
    }

    this.connectionUnstable.value = !connected
    if (connected) {
      this.connectionUnstable.unstableTimer = setTimeout(() => {
        if (this.connectionUnstable.closeTimer) {
          clearTimeout(this.connectionUnstable.closeTimer)
          this.connectionUnstable.closeTimer = null
        }
        // dispatch connectionUnstable !ok
        this.dispatch(connectionUnstable(false))
      }, STABLE_TIMEOUT * 1000)
    } else {
      this.connectionUnstable.closeTimer = setTimeout(() => {
        // stays too long disconnected, close all
        this.dispatch(setDisconnectMessage('disconnect-poor-connection'))
        this.dispatch(gracefulExit())
      }, CLOSE_TIMEOUT * 1000)
      this.connectionUnstable.unstableTimer = setTimeout(() => {
        this.dispatch(connectionUnstable(true))
      }, UNSTABLE_TIMEOUT * 1000)
    }

  }

  _webRTCConnectionOnIceConnectionStateChange = (event) => {
    const rtcPeerConnection = event.target
    logger('=== iceConnectionState', rtcPeerConnection.iceConnectionState)
    switch (rtcPeerConnection.iceConnectionState) {
      case 'new':
        this.dispatch(iceConnectionState('NEW'))
        break
      case 'checking':
        this.dispatch(iceConnectionState('CHECKING'))
        break
      case 'connected':
        this._computeConnectionUnstable(true)
        this.dispatch(iceConnectionState('CONNECTED'))
        // if access state is not yet resolved we are in the 'pending' state
        // the app will remain in this state until access granted/denied is received
        // from the agent (see logic/app.confirmAccessLogic)
        if (this.getState().webrtcConnection.accessState === '') {
          this.dispatch(step(APP_STEP.RTC_WAIT_CONFIRM_ACCESS))
          const accessGrantedRequired = [AGENT_TYPE.ON_DEMAND].includes(Permissions.agentType)
          if (!accessGrantedRequired) {
            // @TODO - check confirm access permissions to see
            // whether accesGranted or accessPending should be send
            this.dispatch(accessGranted())
          } else if (!this.getState().webrtcConnection.accessGranted) {
            this.dispatch(accessPending()) // just show a waiting message
          }
        }
        break
      case 'completed':
        this._computeConnectionUnstable(true)
        this.dispatch(iceConnectionState('COMPLETED'))
        break
      case 'failed':
        this.dispatch(iceConnectionState('FAILED'))
        this.raiseError({ message: 'error-rtc-connection', error: 'iceConnectionState FAILED' })
        break
      case 'disconnected':
        this._computeConnectionUnstable(false)
        this.dispatch(iceConnectionState('DISCONNECTED'))
        break
      case 'closed':
        this.dispatch(iceConnectionState('CLOSED'))
        break
      default:
        break
    }
  }
}
